import { useCallback, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import { addBreadcrumb, captureException, captureEvent } from '@sentry/react';
import { push } from 'connected-react-router';
import SHA256 from 'crypto-js/sha256';
import type { FormikState } from 'formik';
import { Form, Formik } from 'formik';
import LogRocket from 'logrocket';
import qs from 'query-string';
import { useHistory } from 'react-router';
import { Link, useLocation } from 'react-router-dom';
import { css } from 'styled-components';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import Button from 'src/app/components/Elements/Button';
import { DevOnlyRegistrationInfo } from 'src/app/components/Elements/DevOnly/DevOnlyRegistrationInfo';
import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import GenericError from 'src/app/components/Error/Generic';
import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete';
import { FormikPasswordField } from 'src/app/components/Form/FormikPasswordField/FormikPasswordField';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import { useFocusOnFirstFormError } from 'src/app/components/Form/Hooks/useFocusOnFirstFormError';
import { NextStep } from 'src/app/context/AuthContext';
import { useAccount } from 'src/app/hooks/data/account';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import useAmplitude from 'src/app/hooks/useExperiments';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import {
  login as loginPath,
  logout as logoutPath,
  forgotPassword,
  getStarted,
  mfaForm,
  mfaInterstitial,
  passwordResetRequired,
  newOnboardingStep,
  medications,
  openAppLanding,
} from 'src/app/router/paths';
import { getLoginRedirectPath } from 'src/app/router/utils';
import * as actionTypes from 'src/app/state/actions/types';
import { useDispatch } from 'src/app/state/store';
import { COOKIE_IS_LOGGED_IN } from 'src/app/storage/cookieKeys';
import { clearCookie, setCookie } from 'src/app/storage/cookies';
import { HAS_SEEN_MFA_INTERSTIAL_IN_PAST } from 'src/app/storage/localStorageKeys';
import { isHydra } from 'src/app/utils/browser';
import { isMobileBrowser, getOS } from 'src/app/utils/isMobile';
import {
  LOGIN_PAGE_EMAIL_INPUT_ELEMENT,
  LOGIN_PAGE_HEADER_ELEMENT,
  LOGIN_PAGE_PASSWORD_INPUT_ELEMENT,
} from 'src/consts/testIDs';
import { useLogRocketInit } from 'src/hooks/useLogRocketInit';

import { useFacilityByAccountId } from '../../../hooks/data/facility';
import Spinner from '../../Elements/Spinner';
import { EscriptOnboardingStepName } from '../EscriptOnboarding/types';

import { StyledCTA } from './styles';
import validation from './validation';

export interface FormValues {
  email: string;
  password: string;
}

const FormWithFirstFieldErrorFocus: FC<Pick<FormikState<FormValues>, 'errors' | 'isSubmitting' | 'isValidating'>> = ({
  children,
  errors,
  isSubmitting,
  isValidating,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  useFocusOnFirstFormError(formRef, errors, isSubmitting, isValidating, false);

  return <Form ref={formRef}>{children}</Form>;
};

export const Login = () => {
  useSegmentEvent('viewLogin', { shouldTrackOnMount: true });
  const trackEventViewExpiredCheckoutLink = useSegmentEvent('viewExpiredCheckoutLink', { shouldTrackOnMount: false });
  const trackEventSignInCodeProvided = useSegmentEvent('signInCodeProvided', { shouldTrackOnMount: false });
  const trackEventBeginOnboarding = useSegmentEvent('beginOnboarding', { shouldTrackOnMount: false });
  const trackEventAppWebPageEligible = useSegmentEvent('appWebPageEligible', { shouldTrackOnMount: false });
  useLogRocketInit();

  const { login, loginByCode, loggedInUser, isLoggedIn } = useAuth();

  const dispatch = useDispatch();
  const [genericError, setGenericError] = useState<JSX.Element | string>('');
  const location = useLocation();
  const locationState = location.state as { [key: string]: string };
  const referer = locationState ? locationState.referer ?? '' : '';
  const { replace } = useHistory();
  const { search: locationQs } = location;
  const { code, capsuleCode, userUuid: qsUserUuid, flow, method } = qs.parse(location.search);
  const isCodeDetected = qsUserUuid && code && (code as string).split('.').length === 2;
  const isCapsuleCodeDetected = qsUserUuid && capsuleCode && (capsuleCode as string).split('.').length === 2;
  const { track: logRocketTrack } = LogRocket;

  const hasSeenMFABefore = window.localStorage.getItem(HAS_SEEN_MFA_INTERSTIAL_IN_PAST);

  useEffect(() => {
    clearCookie(COOKIE_IS_LOGGED_IN); // Clear SSR redirect cookie.

    window.localStorage.setItem('testItem', 'true');
    setCookie('testCookie', 'true');
  }, []);

  const { fetchVariant } = useAmplitude();

  const {
    data: user,
    isError: isUserError,
    isSuccess: isLoadUserSuccess,
  } = useUser(loggedInUser ? loggedInUser.uuid : '', isLoggedIn);

  useEffect(() => {
    if (user) {
      try {
        LogRocket.identify(user.accountId);
      } catch (err) {
        console.error('Failed to identify users for LR.', err);
      }
    }
  }, [user]);

  const {
    data: account,
    isFetching: isAccountFetching,
    isError: isAccountError,
  } = useAccount(user ? parseInt(user.accountId) : -1, { enabled: Boolean(isLoggedIn), refetchOnWindowFocus: false });

  const { data: facility, isLoading: isFacilityLoading } = useFacilityByAccountId(user ? parseInt(user.accountId) : 0, {
    enabled: !!user,
    refetchOnWindowFocus: false,
  });

  const loginByCheckoutLink = useCallback(async () => {
    if (loggedInUser === undefined) {
      return;
    }

    if ((code || capsuleCode) && qsUserUuid) {
      try {
        const flowQs = flow ? `?flow=${flow}` : '';

        if (loggedInUser !== null) {
          if ((qsUserUuid as string) !== loggedInUser.uuid) {
            console.log('Logging out the current user.');
            return replace(logoutPath.toPath(), { loginQs: `${locationQs}` });
          }
          return replace(`${loginPath.route}${flowQs}`);
        }

        if (capsuleCode) {
          // CapOS is still using QS ?code=, so this excludes CapOS.
          try {
            logRocketTrack(UNIFIED.signInCodeProvided.name);
          } catch (e) {
            console.log(`Encountered error when sending event to LR: ${e}`);
          }

          try {
            trackEventSignInCodeProvided();
          } catch (e) {
            console.log(`Encountered error when sending event: ${e}`);
          }
        }

        console.log('Logging in by checkout code.');
        const authCode = code ? code : capsuleCode;
        const nextStep = await loginByCode(qsUserUuid as string, authCode as string);
        if (nextStep === NextStep.MFA) {
          if (hasSeenMFABefore) {
            return replace(`${mfaForm.route}${flowQs}`);
          }
          return replace(`${mfaInterstitial.route}${flowQs}`);
        }

        const params = `method=capsuleCode&code=${authCode}`;
        const methodQs = flowQs ? `${flowQs}&${params}` : `?${params}`;
        return replace(`${loginPath.route}${methodQs}`);
      } catch (err: any) {
        console.log({ err });
        trackEventViewExpiredCheckoutLink();
        return replace(loginPath.toPath(), { error: 'The checkout link is expired or invalid.' }); // Redirect itself with error, strip QS.
      }
    }
  }, [
    loggedInUser,
    code,
    capsuleCode,
    qsUserUuid,
    flow,
    loginByCode,
    replace,
    locationQs,
    logRocketTrack,
    trackEventSignInCodeProvided,
    hasSeenMFABefore,
    trackEventViewExpiredCheckoutLink,
  ]);

  useEffect(() => {
    loginByCheckoutLink();
  }, [loginByCheckoutLink]);

  const dispatchPushUserThroughRequiredPasswordReset = ({ email }: { email: string }) =>
    dispatch(push(passwordResetRequired.route, { email }));

  const dispatchOnboardingAnalytics = () =>
    trackEventBeginOnboarding({ pagepath: location.pathname, source: 'Sign In Page' });

  useEffect(() => {
    const loginRedirect = async () => {
      // If the password-less login code is in the query string, wait for it to process
      // because the login code can belong to a different user from the current logged-in user.
      if (isCodeDetected || isCapsuleCodeDetected) {
        return;
      }

      // Wait until the loggedInUser loads, if loggedInUser resolves to null then there is no active session.
      if (loggedInUser === undefined) {
        return;
      }

      if (isLoggedIn && loggedInUser && user && !isFacilityLoading) {
        await dispatch({
          type: actionTypes.SEGMENT_IDENTIFY,
          accountId: user.accountId,
        });

        if (flow) {
          return replace(getLoginRedirectPath(locationQs));
        } else {
          console.log('Debugging experiment:', { isLoginSet: user?.isLoginSet, method });
          // Check for custom app adoption experiment.
          if (method === 'capsuleCode') {
            const experimentResult = await fetchVariant({
              experimentKey: 'app-adoption-first-order',
              userId: user.accountId,
              audienceAttributes: {
                facility_code: facility?.code || '',
                browser_type: isMobileBrowser(window.navigator.userAgent) ? 'Mobile' : 'Desktop',
                operating_system: getOS(),
                is_login_set: user.isLoginSet,
              },
            });

            console.log('App adoption variant:', experimentResult);
            if (experimentResult === 'control') {
              trackEventAppWebPageEligible(); // "View Web Page Eligible" event is emitted through Segment
              console.log('App adoption, redirecting to medications.');
              return replace(medications.toPath());
            } else if (experimentResult === 'show-app-adoption-page') {
              console.log('Redirecting to app adoption.');
              return replace(`${openAppLanding.toPath()}?userUuid=${loggedInUser.uuid}&code=${code}`);
            }
          }

          // Check for onboarding flow. Signup source should be escript, and isLoginSet should be false
          if (isHydra() || (isLoadUserSuccess && account?.signupSource === 'escript' && !user.isLoginSet)) {
            return replace(newOnboardingStep.toPath({ stepName: EscriptOnboardingStepName.redirect }));
          }

          if (referer) {
            console.log(`Redirecting to ${referer}!`);
            return replace(referer);
          }

          console.log('Redirecting to medications!');
          return replace(medications.toPath());
        }
      }
    };

    loginRedirect();
  }, [
    dispatch,
    flow,
    isCapsuleCodeDetected,
    isCodeDetected,
    isLoggedIn,
    locationQs,
    loggedInUser,
    method,
    replace,
    referer,
    user,
    facility,
    isFacilityLoading,
    fetchVariant,
    trackEventAppWebPageEligible,
    isLoadUserSuccess,
    account?.signupSource,
  ]);

  useEffect(() => {
    if (isUserError || isAccountError) {
      setGenericError('Something isn’t quite right on our end... Please try again or contact the pharmacy for help.');
    }
  }, [isUserError, isAccountError]);

  useEffect(() => {
    if (locationState) {
      if ('error' in locationState) {
        setGenericError(locationState.error as string);
      } else if ('message' in locationState) {
        setGenericError(locationState.message as string);
      }
    }
  }, [locationState]);

  if (isCodeDetected || isCapsuleCodeDetected) {
    return <Spinner isFullViewportSpinner />;
  } else if ((isAccountFetching || isLoggedIn) && isAccountError === false) {
    return <Spinner centered />;
  }

  return (
    <div className="form-frame py-5">
      <h1 className="h5 center mb-5 font-h-4" data-testid={LOGIN_PAGE_HEADER_ELEMENT}>
        {referer.startsWith('/track/') ? 'Sign in to track your order' : 'Sign in'}
      </h1>

      <Formik
        onSubmit={async (values, { setSubmitting }) => {
          const { email, password } = values;

          setSubmitting(true);

          try {
            captureEvent({
              message: 'Login form submitted',
              level: 'info',
              extra: {
                emailSHA256: SHA256(email).toString(),
                // Never send the entire hash, just the first 5 chars are all we need for debugging.
                passwordSHA256Substr: SHA256(password).toString().substring(0, 5),
              },
              tags: {
                feature: 'auth',
              },
            });

            const nextStep = await login(email, password);

            if (nextStep === NextStep.MFA) {
              const flowQs = flow ? `?flow=${flow}` : '';

              if (hasSeenMFABefore) {
                dispatch(push(`${mfaForm.route}${flowQs}`));
              } else {
                dispatch(push(`${mfaInterstitial.route}${flowQs}`));
              }
            } else if (nextStep === NextStep.RESET_PASSWORD) {
              /**
               * Handle built-in force password reset flow.
               *
               * To trigger this flow, use AWS AdminResetUserPassword command with
               * ClientMetadata set to {'suppressEmail': 'true'}. As a result, Cognito user's
               * status changes to RESET_REQUIRED and the Custom Email Sender Lambda triggers.
               * The password reset email that usually gets sent out when setting the status to
               * RESET_REQUIRED is going to be suppressed if `suppressEmail` in ClientMetadata is `true`.
               *
               * You can also set the user's status to RESET_REQUIRED on the AWS Console GUI, but
               * this way has no way to suppress the password reset email.
               */
              dispatchPushUserThroughRequiredPasswordReset({ email });
            }
          } catch (error: any) {
            if (error.name === 'PasswordResetRequiredException') {
              dispatchPushUserThroughRequiredPasswordReset({ email });
            } else if (error.message.includes('RESET_REQUIRED')) {
              /*
              For Custom password reset flow with custom:reset_required attribute.

              This flow is different from the built-in force password reset flow.
              The exception `RESET_REQUIRED` is thrown from pre-auth lambda when reset_required
              user attribute is set to `true` in Cognito. The key difference is that the built-in
              flow only triggers after user types in their current password correctly. On the other hand,
              this custom flow triggers even if the user types in the wrong password.
              */
              dispatchPushUserThroughRequiredPasswordReset({ email });
            } else if (error.name === 'NotAuthorizedException') {
              if (error.message === 'Password attempts exceeded') {
                setGenericError(
                  <>
                    You’ve attempted to login too many times. Please return in 10 minutes to try again or{' '}
                    <Link to={forgotPassword.toPath()}>reset your password</Link>.
                  </>
                );
              } else {
                setGenericError(
                  'Oops, looks like your email or password doesn’t match what we have on file. Please try again or contact the pharmacy for help.'
                );
              }
            } else {
              setGenericError(
                'Something isn’t quite right on our end... Please try again or contact the pharmacy for help.'
              );
              addBreadcrumb({
                data: { email },
                message: 'Handled error without "caring" UI when attempting to login on the login page.',
              });

              captureException(error, {
                contexts: {
                  request: {
                    emailSHA256: SHA256(email).toString(),
                    passwordSHA256Substr: SHA256(password).toString().substring(0, 5),
                  },
                },
                tags: {
                  feature: 'auth',
                },
              });
            }
          }
          setSubmitting(false);
        }}
        initialValues={validation.initialValues}
        validateOnBlur={false}
        validateOnChange={true}
        validationSchema={validation.schema}
      >
        {({ errors, isSubmitting, isValid, isValidating }) => {
          return (
            <FormWithFirstFieldErrorFocus errors={errors} isSubmitting={isSubmitting} isValidating={isValidating}>
              {genericError && (
                <GenericError
                  errors={{
                    _generic: genericError,
                  }}
                />
              )}
              <SegmentFormIncomplete />

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: ${({ theme }) => theme.space.s3};
                  margin-bottom: ${({ theme }) => theme.space.s3};
                `}
              >
                <FormikTextField
                  name="email"
                  type="email"
                  label={{ content: 'Email', isHidden: false }}
                  autoComplete="username"
                  data-testid={LOGIN_PAGE_EMAIL_INPUT_ELEMENT}
                />

                <FormikPasswordField
                  label={{ content: 'Password', isHidden: false }}
                  autoComplete="current-password"
                  data-testid={LOGIN_PAGE_PASSWORD_INPUT_ELEMENT}
                />
              </div>

              <p>
                <Button
                  type="submit"
                  size="medium"
                  style="primary"
                  block
                  disabled={isSubmitting || !isValid}
                  data-cy="sign-in"
                >
                  SIGN IN
                </Button>
              </p>
              <StyledCTA>
                Don’t have an account? –{' '}
                <LinkWithQuery to={getStarted.toPath()} onClick={dispatchOnboardingAnalytics}>
                  Get started
                </LinkWithQuery>
              </StyledCTA>
              <p className="center">
                <LinkWithQuery to={forgotPassword.toPath()}>Forgot your password?</LinkWithQuery>
              </p>
            </FormWithFirstFieldErrorFocus>
          );
        }}
      </Formik>

      <DevOnlyRegistrationInfo />
    </div>
  );
};
